import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    useTheme,
    useMediaQuery,
    Typography,
} from '@mui/material';
import CommonCard from '../../../../components/card/CommonCard';
import { Next, Prev } from '../../../../assets/images';
import { logAPI } from '../../../../actions/imsServer';
import { useTranslation } from 'react-i18next';

import { UserInfoCard } from '../cards';
import StatisticsChart from '../../../../components/charts/barChart/StatisticsChart';
import RewardHistoryItem from '../../../../components/rewardHistoryItem';
import { formatDateTimeDisplay, getUserRole } from '../../../../utils/base';
import { useNavigate } from 'react-router';
import { Memo } from '../../../../assets/images';
import LevelProgressCard from '../../../../components/levelProgressCard';
import { API_PAGE_SIZE } from '../../../../constants';

export const BasicInfoTab = ({ userData }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const [currentDate, setCurrentDate] = useState(new Date());
    const [pointLogData, setPointLogData] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = API_PAGE_SIZE;
    const [meInfo, setMeInfo] = useState(() => {
        try {
            const stored = localStorage.getItem('meInfo');
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error('Failed to parse meInfo from localStorage:', error);
            return null;
        }
    });

    // currentDate 변경 시 pointLogData 초기화 및 첫 페이지 로드
    useEffect(() => {
        // userData?.id가 없으면 실행하지 않음
        if (!userData?.id) {
            setPointLogData([]);
            setTotalCount(0);
            setHasMore(false);
            return;
        }

        let isCancelled = false;

        const loadPointLogData = async () => {
            // 선택된 달 시작일과 종료일 계산
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const startDate = new Date(year, month, 1);
            const endDate = new Date(year, month + 1, 0, 23, 59, 59);

            try {
                const pointLogDataResponse = await logAPI.getPointLogs({
                    userId: userData.id,
                    type: 0,
                    startTime: startDate.toISOString(),
                    endTime: endDate.toISOString(),
                    offset: 0,
                });

                // 컴포넌트가 언마운트되었거나 취소된 경우 상태 업데이트하지 않음
                if (isCancelled) return;

                if (pointLogDataResponse?.data) {
                    const logs = pointLogDataResponse.data.logs || [];
                    const total = pointLogDataResponse.data.total || 0;
                    setPointLogData(logs);
                    setTotalCount(total);
                    setHasMore(logs.length < total);
                } else {
                    setPointLogData([]);
                    setTotalCount(0);
                    setHasMore(false);
                }
            } catch (error) {
                if (isCancelled) return;
                console.error('Failed to load point log data:', error);
                setPointLogData([]);
                setTotalCount(0);
                setHasMore(false);
            }
        };

        setPointLogData([]);
        setTotalCount(0);
        setHasMore(true);
        loadPointLogData();

        // cleanup 함수: 컴포넌트 언마운트 시 취소 플래그 설정
        return () => {
            isCancelled = true;
        };
    }, [currentDate, userData?.id]);

    // 더 많은 데이터 로드 함수
    const loadMorePointLogs = useCallback(async () => {
        if (!userData?.id || isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);
        try {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const startDate = new Date(year, month, 1);
            const endDate = new Date(year, month + 1, 0, 23, 59, 59);

            const pointLogDataResponse = await logAPI.getPointLogs({
                userId: userData.id,
                type: 0,
                startTime: startDate.toISOString(),
                endTime: endDate.toISOString(),
                offset: pointLogData.length,
            });

            if (pointLogDataResponse?.data) {
                const newLogs = pointLogDataResponse.data.logs || [];
                const total = pointLogDataResponse.data.total || totalCount;
                if (newLogs.length > 0) {
                    setPointLogData(prev => [...prev, ...newLogs]);
                    setTotalCount(total);
                    setHasMore(pointLogData.length + newLogs.length < total);
                } else {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Failed to load more point log data:', error);
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [userData?.id, currentDate, pointLogData.length, isLoadingMore, hasMore, totalCount]);

    // 이달의 차트 데이터 생성
    const monthChartData = React.useMemo(() => {
        // 선택된 달의 모든 날짜 생성
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const chartDataMap = new Map();

        // 모든 날짜를 0으로 초기화
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            chartDataMap.set(dateStr, { day: dateStr, value: 0 });
        }

        // pointLogData를 날짜별로 합산
        pointLogData.forEach(log => {
            if (log.time) {
                const dateStr = log.time.substring(0, 10); // YYYY-MM-DD 형식
                if (chartDataMap.has(dateStr)) {
                    chartDataMap.get(dateStr).value += log.point || 0;
                }
            }
        });
        return Array.from(chartDataMap.values());
    }, [pointLogData, currentDate]);

    // 최대값, 최소값, 평균값 계산
    const yAxisData = React.useMemo(() => {
        if (!monthChartData || monthChartData.length === 0) {
            return { maxValue: 0, minValue: 0, averageValue: 0 };
        }

        const values = monthChartData.map(item => item.value);
        const maxValue = Math.max(...values, 0);
        const minValue = 0;
        const averageValue = values.reduce((sum, val) => sum + val, 0) / values.length;

        return {
            maxValue,
            minValue,
            averageValue
        };
    }, [monthChartData]);

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        const now = new Date();
        const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);

        // 현재 월을 넘어가지 않도록 제한
        if (nextMonth.getFullYear() < now.getFullYear() ||
            (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() <= now.getMonth())) {
            setCurrentDate(nextMonth);
        }
    };

    const currentDateString = formatDateTimeDisplay(currentDate, 'yearMonth');

    // 다음 버튼 비활성화 여부 확인
    const now = new Date();
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    const isNextDisabled = nextMonth.getFullYear() > now.getFullYear() ||
        (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() > now.getMonth());

    // 현재 선택된 달의 포인트 로그 필터링
    const filteredPointLogs = React.useMemo(() => {
        if (!pointLogData || pointLogData.length === 0) return [];

        const selectedYear = currentDate.getFullYear();
        const selectedMonth = currentDate.getMonth();

        return pointLogData.filter(log => {
            if (!log.time) return false;
            const logDate = new Date(log.time);
            return logDate.getFullYear() === selectedYear && logDate.getMonth() === selectedMonth;
        });
    }, [pointLogData, currentDate]);

    // Infinite scroll을 위한 상태
    const scrollContainerRef = useRef(null);

    // 표시할 로그 목록 (모든 filteredPointLogs 표시)
    const displayedLogs = filteredPointLogs;

    // 스크롤 이벤트 핸들러
    const handleScroll = useCallback((e) => {
        const container = e.target;
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        // 하단 200px 이내에 도달하면 더 많은 데이터 로드
        if (scrollHeight - scrollTop - clientHeight < 200) {
            loadMorePointLogs();
        }
    }, [loadMorePointLogs]);

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, overflow: 'hidden' }}>
            {/* 메인 콘텐츠 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* 좌우 레이아웃 */}
                <Box sx={{
                    display: 'flex',
                    gap: '10px',
                    width: '100%',
                    alignItems: 'stretch',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden'
                }}>
                    {/* 좌측 컬럼 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        flex: '1 1 50%',
                        minHeight: 0,
                        overflow: 'hidden'
                    }}>
                        {/* 좌측 상단: UserInfoCard */}
                        {userData &&
                            (getUserRole(meInfo.grade) === 'superadmin' ? (
                                <LevelProgressCard
                                    level={userData?.level || 0}
                                    exp={userData?.exp || 0}
                                />
                            ) :
                                getUserRole(meInfo.grade) === 'admin' && (
                                    <Box sx={{ position: 'relative' }}>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                width: '20px',
                                                height: '20px',
                                                cursor: 'pointer',
                                                zIndex: 10,
                                            }}
                                            onClick={(e) => {
                                                navigate('/users/edit', { state: { userData } });
                                            }}
                                        >
                                            <Memo />
                                        </Box>
                                        <UserInfoCard userData={userData} hidePoint={true} />
                                    </Box>
                                ))}

                        {/* 좌측 하단: 경험치 차트 */}
                        <CommonCard>
                            <StatisticsChart
                                activeView="month"
                                chartData={monthChartData}
                                yAxisData={yAxisData}
                                barColorType="average"
                            />
                        </CommonCard>
                    </Box>

                    {/* 우측 컬럼 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        flex: '1 1 50%',
                        minHeight: 0,
                        maxHeight: '100%',
                        overflow: 'hidden',
                        paddingTop: '10px'
                    }}>
                        {/* 우측: 히스토리 */}
                        {displayedLogs.length > 0 && (
                            <Box
                                ref={scrollContainerRef}
                                onScroll={handleScroll}
                                sx={{
                                    width: '100%',
                                    height: '580px', // 8개 아이템만 보이도록 고정 높이 (60px * 8 + 10px * 7) + alpha
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                    '&::-webkit-scrollbar': {
                                        width: '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: theme.palette.figma.white[20],
                                        borderRadius: '3px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        backgroundColor: theme.palette.figma.white[40],
                                    },
                                }}
                            >
                                {displayedLogs.map((log, index) => (
                                    <RewardHistoryItem key={log.id || index} item={log} showSign={false} />
                                ))}
                                {isLoadingMore && (
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        padding: '10px',
                                        color: theme.palette.figma.white[60]
                                    }}>
                                        <Typography sx={{ fontSize: '12px' }}>로딩 중...</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Date Indicator - 화면 맨 아래 가운데 */}
                <Box sx={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px 0'
                }}>
                    <Box
                        onClick={handlePrevMonth}
                        sx={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Prev color={theme.palette.figma.white[80]} size={20} />
                    </Box>
                    <Typography sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: theme.palette.figma.white[80],
                        letterSpacing: '-0.48px',
                        whiteSpace: 'nowrap'
                    }}>
                        {currentDateString}
                    </Typography>
                    <Box
                        onClick={() => !isNextDisabled && handleNextMonth()}
                        sx={{
                            width: '20px',
                            height: '20px',
                            cursor: isNextDisabled ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isNextDisabled ? 0.3 : 1
                        }}
                    >
                        <Next color={theme.palette.figma.white[80]} size={20} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
